import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { assignmentToRole } from 'contexts/AuthContext';
import { ROUTES } from 'constants/routePaths';
import { resolveLandingPath } from 'util/landing';
import Loader from 'components/loader/Loader';
import { useAuth } from 'hooks/useAuth';
import { toastPermission } from 'util/toast';
import {
  isCenterAdminRole,
  isJuryMemberRole,
  isMasterRole,
  isMentorRole,
  isStartupFounderRole,
  isSuperAdminRole,
  roleMatchesAny,
} from 'util/access';

// `allowedRoles` values are CAPABILITY KEYS, not literal role-name matches.
//
// Admin capabilities resolve from the PRINCIPAL (the backend's `role_is_admin`
// flag + scope), so a route gated to `center_admin` keeps working after the
// center-admin role is renamed — no route change needed. Reserved assignment
// roles (jury/mentor/founder) resolve from the held role NAME (fixed system
// roles that are never renamed).
const PRINCIPAL_FAMILY = {
  superadmin: (p) => isSuperAdminRole(p.role),
  super_admin: (p) => isSuperAdminRole(p.role),
  'super admin': (p) => isSuperAdminRole(p.role),
  // 'master' gates to the platform master ONLY (an org super admin must not pass
  // master-only routes); super-admin-family routes still include master via
  // isSuperAdminRole.
  master: (p) => isMasterRole(p.role),
  // Center-admin capability — FLAG-based, so it survives a role rename.
  center_admin: (p) => isCenterAdminRole(p),
  'center admin': (p) => isCenterAdminRole(p),
  centeradmin: (p) => isCenterAdminRole(p),
};

const NAME_FAMILY = {
  jury_member: isJuryMemberRole,
  jurymember: isJuryMemberRole,
  jury: isJuryMemberRole,
  startup_founder: isStartupFounderRole,
  startupfounder: isStartupFounderRole,
  founder: isStartupFounderRole,
  mentor: isMentorRole,
};

/** True if the caller satisfies ANY of `allowedRoles`. */
const roleAllowed = (principal, heldRoles, allowedRoles) =>
  allowedRoles.some((r) => {
    const key = String(r).toLowerCase();
    const principalFamily = PRINCIPAL_FAMILY[key];
    if (principalFamily) return principalFamily(principal);
    const nameFamily = NAME_FAMILY[key];
    if (nameFamily) return heldRoles.some((held) => nameFamily(held));
    return heldRoles.some((held) => roleMatchesAny(held, [r]));
  });

/**
 * Redirect that says why.
 *
 * A denied route used to bounce the user to their landing page in silence, so
 * clicking a card on a module they only hold READ on dumped them on Workspace
 * with no explanation — indistinguishable from the app losing their place.
 * The guard still has to navigate away (there is no page to render), but never
 * without telling them.
 *
 * Own component so the effect obeys the rules of hooks regardless of which
 * early return above fired.
 */
function DeniedRedirect({ to, action }) {
  useEffect(() => {
    // toastPermission, not toastInfo: one denial is one toast. StrictMode
    // double-invokes this effect in development, which is how a single click
    // produced two identical toasts.
    toastPermission(
      action === 'write'
        ? 'You have read-only permission for this module.'
        : "You don't have permission to open that page.",
    );
  }, [action]);
  return <Navigate to={to} replace />;
}

/**
 * Guards authenticated routes. Optionally restricts by capability (`allowedRoles`,
 * resolved via the principal/flag) and/or a resource×action permission check
 * (`can(resource, action)` from AuthContext, backed by the real
 * `/auth/me/permissions` RBAC matrix).
 *
 * `match` decides how the two combine when BOTH are given: 'all' (default) needs
 * the caller to satisfy each, 'any' needs only one. Use 'any' for a module a
 * permission can unlock on its own — e.g. Evaluation Matrix opens for any admin
 * OR for any role holding `evaluations:write` (which Programs write implies).
 * @param {{ allowedRoles?: string[], resource?: string, action?: 'read'|'write',
 *          match?: 'all'|'any' }} props
 */
export function ProtectedRoute({
  allowedRoles,
  resource,
  action = 'read',
  match = 'all',
  // Roles a page never belongs to, whatever verbs they hold — a founder's
  // `documents:read` is for their own startup, not the centre's Document
  // Verification. Excluded only when EVERY role the user holds is excluded:
  // multi-role access is the union, so a founder who also holds a staff role
  // keeps that role's page (and a centre admin who founded a startup too).
  excludeRoles,
  // When this query param is present the resource×action gate is skipped — used
  // by the program wizard's in-collaboration create (`?collab=<groupId>`), which
  // an accepted collaboration member may open without center-wide
  // `applications:write`. The backend still re-authorises membership on save, so
  // this only opens the *page*, never the underlying action.
  bypassParam,
}) {
  const {
    isAuthenticated, role, principal, can, user, permissions,
    hasResourceGrant, isInitialising,
  } = useAuth();
  const permsLoaded = Boolean(permissions);
  const location = useLocation();
  const bypassResource =
    Boolean(bypassParam) && Boolean(new URLSearchParams(location.search).get(bypassParam));

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // First-login gate: an account provisioned with a temporary password must set a
  // real one before touching anything else. Exempt the reset screen itself so it
  // can actually render.
  if (user?.mustChangePassword && location.pathname !== ROUTES.FORCE_PASSWORD_CHANGE) {
    return <Navigate to={ROUTES.FORCE_PASSWORD_CHANGE} replace />;
  }

  // A resource gate below depends on `can()`/`hasResourceGrant()` — permissions
  // and collaboration grants that only arrive after AuthContext's post-reload
  // network round-trip (see its revalidation effect). On a hard reload of a
  // resource-gated route, `isInitialising` is still true at first render; hold
  // here instead of evaluating the gate against not-yet-loaded (empty) data,
  // which would otherwise bounce a collaborator to the dashboard every reload.
  if (resource && !bypassResource && isInitialising) {
    return <Loader fullscreen label="Loading…" />;
  }

  // A multi-role user (e.g. mentor + jury) may reach a route gated to ANY role
  // they HOLD, not just their currently-active one — so the merged mentor/jury
  // panel can open both sets of modules without switching. `assignments` are the
  // extra panels held (mentor/jury); map them to their route-guard role family.
  const heldRoles = [role, ...(user?.assignments || []).map((a) => assignmentToRole(a))];

  // Only the criteria actually supplied are evaluated, so a route passing just
  // one of them behaves identically under either `match` mode.
  const checks = [];
  if (allowedRoles?.length) checks.push(roleAllowed(principal, heldRoles, allowedRoles));
  // A collaborator holding a matching record grant passes the resource gate too,
  // so they can open the module list/detail (which then shows only their shared
  // records). Additive — a caller who already passed `can()` is unaffected.
  if (resource && !bypassResource) checks.push(can(resource, action) || hasResourceGrant(resource, action));
  const heldRoleNames = user?.roles?.length ? user.roles : [role];
  const excluded = Boolean(excludeRoles?.length)
    && heldRoleNames.every((name) => roleAllowed(principal, [name], excludeRoles));
  const allowed = !excluded && (checks.length === 0
    || (match === 'any' ? checks.some(Boolean) : checks.every(Boolean)));
  if (!allowed) {
    // Not simply `/dashboard`: the dashboard is a permission too, so bouncing a
    // user who lacks it back there would loop between this guard and
    // DashboardHome's own redirect. Send them to the first module they hold.
    const landing = resolveLandingPath({
      can,
      role,
      principal,
      hasCenter: Boolean(user?.center_id ?? user?.centerId),
      assignments: user?.assignments,
      roleNames: user?.roles,
      hasResourceGrant,
      permsLoaded,
    });
    return <DeniedRedirect to={landing || ROUTES.NO_MODULES} action={resource ? action : undefined} />;
  }

  return <Outlet />;
}

export default ProtectedRoute;
